'use client';

import { Loader2 } from 'lucide-react';
import { useFormatter, useTranslations } from 'next-intl';
import { priceTargetCents, useDeletePriceAlert } from '@/lib/api/priceAlerts';
import { Link } from '@/lib/routing';
import { Icon } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';
import { formatCents } from '@/lib/utils/formatAmount';
import type { PriceAlert } from '../types';
import {
  conditionSymbol,
  formatUsdCompact,
  isOpenInterestCondition,
  isPriceCondition,
  isWhaleCondition,
} from '../utils';

interface PriceAlertRowProps {
  alert: PriceAlert;
  onEdit: () => void;
}

export function PriceAlertRow({ alert, onEdit }: PriceAlertRowProps) {
  const t = useTranslations('priceAlerts');
  const format = useFormatter();
  const deleteMutation = useDeletePriceAlert();
  const isDeleting = deleteMutation.isPending;

  const handleDelete = () => {
    deleteMutation.mutate(alert.id);
  };

  const isYes = alert.side === 'YES';
  const symbol = conditionSymbol(alert.conditionType);
  const isPrice = isPriceCondition(alert.conditionType);
  const usdValue = formatUsdCompact(alert.targetValue);

  const getTargetText = () => {
    if (isPrice) {
      return t('row.priceTarget', {
        symbol,
        value: formatCents(priceTargetCents(alert.targetValue)),
      });
    }
    if (isWhaleCondition(alert.conditionType)) {
      return t('row.whaleTarget', { value: usdValue });
    }
    if (isOpenInterestCondition(alert.conditionType)) {
      return t('row.oiTarget', { symbol, value: usdValue });
    }
    return t('row.volumeTarget', { symbol, value: usdValue });
  };
  const targetText = getTargetText();

  const getCurrentText = () => {
    if (alert.currentValue === null || alert.currentValue === undefined) return null;
    if (isPrice) {
      return t('row.now', { value: formatCents(priceTargetCents(alert.currentValue)) });
    }
    return t('row.now', { value: formatUsdCompact(alert.currentValue) });
  };
  const currentText = getCurrentText();

  const lastSentText = alert.lastSentAt
    ? format.dateTime(new Date(alert.lastSentAt), {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'UTC',
      })
    : t('lastSent.never');

  const statusColor = alert.status === 'ACTIVE' ? 'text-v2-green' : 'text-v2-text-secondary';
  const STATUS_LABEL_KEYS = {
    ACTIVE: 'status.active',
    TRIGGERED: 'status.triggered',
    CANCELLED: 'status.cancelled',
    EXPIRED: 'status.expired',
  } as const;
  const statusLabel = t(STATUS_LABEL_KEYS[alert.status]);

  const isPerTrigger = alert.repeatMode === 'PER_TRIGGER';
  const typeLabel = alert.repeatMode === 'ONCE' ? t('type.once') : t('type.everyTrigger');

  const normalizedChannel = alert.lastSentChannel?.toUpperCase();
  const lastSentChannelLabel =
    normalizedChannel === 'TELEGRAM' || normalizedChannel === 'PUSH'
      ? t(`channels.${normalizedChannel}`)
      : alert.lastSentChannel;

  return (
    <div className="col-span-full grid grid-cols-subgrid items-center gap-x-4 rounded-2xl px-2 py-4">
      {/* Outcome */}
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex items-center gap-1">
          <span className="truncate text-xs leading-4 font-normal text-v2-text-primary">
            {alert.outcomeName?.trim() || alert.note}
          </span>
          <span
            className={cn(
              'shrink-0 rounded-lg px-2 py-0.5 text-xs leading-4 font-normal',
              isYes ? 'bg-v2-green-surface text-v2-green' : 'bg-v2-red-surface text-red-400',
            )}
          >
            {isYes ? t('side.yes') : t('side.no')}
          </span>
        </div>
        {alert.eventSlug ? (
          <Link
            href={`/event/${alert.eventSlug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate text-[12px] leading-[150%] text-v2-text-secondary transition-colors hover:text-v2-text-primary hover:underline"
          >
            {alert.eventName}
          </Link>
        ) : (
          <span className="truncate text-[12px] leading-[150%] text-v2-text-secondary">
            {alert.eventName}
          </span>
        )}
      </div>

      {/* Target */}
      <div className="flex flex-col items-end gap-0.5">
        <span className="whitespace-nowrap text-xs leading-4 font-normal text-v2-text-primary">
          {targetText}
        </span>
        {currentText && (
          <span className="whitespace-nowrap text-[12px] leading-[150%] text-v2-text-secondary">
            {currentText}
          </span>
        )}
      </div>

      {/* Type */}
      <div className="flex justify-end">
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-lg px-2 py-1',
            isPerTrigger ? 'bg-v2-bg-blue-30' : 'bg-v2-btn-secondary dark:bg-v2-surface-tertiary',
          )}
        >
          <Icon name={isPerTrigger ? 'Swap' : 'Clock'} size={12} className="text-v2-text-primary" />
          <span className="whitespace-nowrap text-[12px] leading-[115%] text-v2-text-primary">
            {typeLabel}
          </span>
        </span>
      </div>

      {/* Last sent */}
      <div className="flex flex-col items-end gap-0.5">
        <span className="whitespace-nowrap text-[12px] leading-[115%] text-v2-text-primary">
          {lastSentText}
        </span>
        {lastSentChannelLabel && (
          <span className="whitespace-nowrap text-[12px] leading-[150%] text-v2-text-secondary">
            {lastSentChannelLabel}
          </span>
        )}
      </div>

      {/* Status */}
      <div className="flex justify-end">
        <span className={cn('whitespace-nowrap text-[12px] leading-[115%]', statusColor)}>
          {statusLabel}
        </span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          onClick={onEdit}
          aria-label={t('row.editAria')}
          className="flex size-8 cursor-pointer items-center justify-center rounded-[10px] text-v2-icon-primary transition-colors hover:bg-v2-btn-secondary hover:text-v2-icon-primary-hover"
        >
          <Icon name="Edit" size={16} />
        </button>
        <button
          type="button"
          onClick={handleDelete}
          disabled={isDeleting}
          aria-label={t('row.deleteAria')}
          aria-busy={isDeleting || undefined}
          className="flex size-8 cursor-pointer items-center justify-center rounded-[10px] text-v2-icon-primary transition-colors hover:bg-v2-btn-secondary hover:text-v2-icon-primary-hover disabled:cursor-default disabled:hover:bg-transparent"
        >
          {isDeleting ? (
            <Loader2 className="size-4 animate-spin text-v2-text-secondary" />
          ) : (
            <Icon name="Trash" size={16} />
          )}
        </button>
      </div>
    </div>
  );
}
